// Farming unit editor (New Season): three tabs over one map.
// - Meta data / Sensors: the farming unit itself, edited as production's
//   farming unit modal edits it (farmingUnitEditRules.js) and saved through
//   the same V1 update_farming_unit action.
// - Fields: fields & blocks; saved by sending the full desired layout to
//   PUT v2/.../layout, which re-reads the tree
//   (newSeasonActions.save_new_season_layout).
// Each tab saves on its own ("Save changes" saves the open tab); unsaved edits
// of the other tabs stay, and a re-read tree replaces only the saved parts.
const React = require('react');
const { useState, useEffect, useMemo, useCallback, useRef } = require('react');
const { useDispatch, useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const kmlManager = require('kmlManager');
const GeneralClasses = require('GeneralClasses');
const distributionActions = require('distributionActions');
const {
  save_new_season_layout,
  load_new_season_distributions,
} = require('@actions/newSeasonActions');
const layout = require('./layoutHelpers');
const unitRules = require('./farmingUnitEditRules');
const { findV2Distribution } = require('./v2Distributions');
const { useV1FarmingUnitWriteStatus } = require('./v1WriteStatus');
const BlockList = require('./BlockList');
const BlocksMap = require('./BlocksMap');
const FarmingUnitMetaTab = require('./FarmingUnitMetaTab');
const FarmingUnitSensorsTab = require('./FarmingUnitSensorsTab');
const { farmingUnitSensorMarkers } = FarmingUnitSensorsTab;
const {
  ConfirmDeleteBlockModal,
  useToast,
  Toast,
  SegmentedTabs,
} = require('./NewSeasonUi');

const WRITE_ROLES = ['admin', 'moderator'];

const TABS = { META: 'meta', FIELDS: 'fields', SENSORS: 'sensors' };

const trackMixpanel = (event, properties) => {
  if (typeof mixpanel !== 'undefined') {
    mixpanel.track(event, properties);
  }
};

// The distribution's name from GET v2/distributions (never the V1 store).
const useDistributionName = (distributionId) => {
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  return findV2Distribution(distributions, distributionId)?.name;
};

const isEditingText = (target) =>
  ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) ||
  target?.isContentEditable;

// One tab's unsaved-edits state. A re-read tree replaces the tab's draft
// unless it has unsaved edits; while the tab's own save is in flight
// (`savingRef`) its draft is replaced anyway, by what was just saved.
const useEditPart = () => {
  const [isDirty, setIsDirtyState] = useState(false);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const setDirty = useCallback((value) => {
    dirtyRef.current = value;
    setIsDirtyState(value);
  }, []);
  // Called once per re-read tree: tells whether to keep the draft.
  const keepsDraftOnReread = useCallback(() => {
    const keeps = dirtyRef.current && !savingRef.current;
    savingRef.current = false;
    if (!keeps) {
      setDirty(false);
    }
    return keeps;
  }, [setDirty]);
  return useMemo(
    () => ({ isDirty, setDirty, savingRef, keepsDraftOnReread }),
    [isDirty, setDirty, keepsDraftOnReread],
  );
};

// The draft layout (Fields tab).
const useDraft = (tree, part) => {
  const [draft, setDraftState] = useState(() => layout.draftFromTree(tree));
  const { setDirty, keepsDraftOnReread } = part;
  useEffect(() => {
    if (!keepsDraftOnReread()) {
      setDraftState(layout.draftFromTree(tree));
    }
  }, [tree, keepsDraftOnReread]);
  const updateDraft = useCallback(
    (update) => {
      setDraftState((previous) => update(previous));
      setDirty(true);
    },
    [setDirty],
  );
  return [draft, updateDraft];
};

const unitFromTree = (tree) =>
  tree.farming_unit
    ? unitRules.farmingUnitForEditing(
        tree.farming_unit,
        tree.role,
        GeneralClasses,
      )
    : null;

// The farming unit draft: basic info / water treatment (Meta data tab,
// `metaPart`) and sensors (Sensors tab, `sensorsPart`). `original` is the
// unit as stored: the other tab's part of each save body, and the Mixpanel
// change list's baseline.
const useUnitDraft = (tree, metaPart, sensorsPart) => {
  const [unit, setUnit] = useState(() => unitFromTree(tree));
  const [original, setOriginal] = useState(() => unitFromTree(tree));
  const keepsMeta = metaPart.keepsDraftOnReread;
  const keepsSensors = sensorsPart.keepsDraftOnReread;
  useEffect(() => {
    const dirty = { meta: keepsMeta(), sensors: keepsSensors() };
    const fromTree = unitFromTree(tree);
    setOriginal(unitFromTree(tree));
    setUnit((current) =>
      fromTree
        ? unitRules.rebaseFarmingUnitDraft(fromTree, current, dirty)
        : null,
    );
  }, [tree, keepsMeta, keepsSensors]);
  const setMetaDirty = metaPart.setDirty;
  const setSensorsDirty = sensorsPart.setDirty;
  const onChange = useCallback(
    (path, value) => {
      setUnit((previous) =>
        unitRules.setFarmingUnitProperty(previous, path, value),
      );
      setMetaDirty(true);
    },
    [setMetaDirty],
  );
  const onChangeSensors = useCallback(
    (update) => {
      setUnit((previous) => ({
        ...previous,
        sensors: update(previous.sensors),
      }));
      setSensorsDirty(true);
    },
    [setSensorsDirty],
  );
  return { unit, original, onChange, onChangeSensors };
};

const useSelection = () => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedFieldKey, setSelectedFieldKey] = useState(null);
  const selectBlock = useCallback((key, event) => {
    setSelectedFieldKey(null);
    const isMulti = event?.shiftKey || event?.metaKey || event?.ctrlKey;
    setSelectedKeys((previous) => {
      if (!isMulti) {
        return [key];
      }
      return previous.includes(key)
        ? previous.filter((current) => current !== key)
        : previous.concat([key]);
    });
  }, []);
  const selectField = useCallback((key) => {
    setSelectedKeys([]);
    setSelectedFieldKey(key);
  }, []);
  const clear = useCallback(() => {
    setSelectedKeys([]);
    setSelectedFieldKey(null);
  }, []);
  return {
    selectedKeys,
    setSelectedKeys,
    selectedFieldKey,
    selectBlock,
    selectField,
    clear,
  };
};

// Delete/Backspace on selected blocks (outside text inputs) asks to delete them.
const useDeleteKey = (selectedKeys, canEdit, onRequestDelete) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (
        !['Delete', 'Backspace'].includes(event.key) ||
        isEditingText(event.target)
      ) {
        return;
      }
      if (canEdit && selectedKeys.length > 0) {
        event.preventDefault();
        onRequestDelete();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedKeys, canEdit, onRequestDelete]);
};

const KmzDropZone = ({ disabled, onAreas }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const readFiles = (files) => {
    if (!disabled && files?.length) {
      kmlManager.onFileSelection({ target: { files } }, (polygons) =>
        onAreas(polygons?.dummy_id?.areas || []),
      );
    }
  };
  return (
    <div
      className={'ns-drop-zone' + (disabled ? ' ns-disabled' : '')}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        readFiles(event.dataTransfer.files);
      }}
    >
      <div className='ns-drop-zone-text'>{t('new_season_drop_kmz')}</div>
      <div className='ns-drop-zone-text ns-muted'>{t('new_season_or')}</div>
      <button
        type='button'
        className='ns-drop-zone-text ns-link-btn'
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {t('new_season_browse')}
      </button>
      <input
        ref={inputRef}
        type='file'
        accept='.kml,.kmz'
        hidden
        onChange={(event) => {
          readFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
};

// What the confirm modal is about to delete: the selected blocks or a field.
const deleteRequestOf = (selection) =>
  selection.selectedFieldKey
    ? { fieldKey: selection.selectedFieldKey }
    : selection.selectedKeys.length
      ? { blockKeys: selection.selectedKeys }
      : null;

const useLayoutHandlers = ({
  updateDraft,
  selection,
  setDeleteRequest,
  showToast,
  t,
}) => {
  const blockPrefix = t('new_season_block_prefix');
  const fieldPrefix = t('new_season_field_prefix');
  return useMemo(
    () => ({
      onSelectBlock: selection.selectBlock,
      onSelectField: selection.selectField,
      onRenameBlock: (key, name) =>
        updateDraft((draft) => layout.renameBlock(draft, key, name)),
      onRenameField: (key, name) =>
        updateDraft((draft) => layout.renameField(draft, key, name)),
      onMoveBlocks: (blockKeys, fieldKey) =>
        blockKeys.length > 0 &&
        updateDraft((draft) => layout.moveBlocks(draft, blockKeys, fieldKey)),
      onRequestDelete: () => setDeleteRequest(deleteRequestOf(selection)),
      onAreasDrawn: (areasList) =>
        updateDraft((draft) =>
          areasList.reduce(
            (current, areas) =>
              layout.addDrawnBlock(current, [areas], blockPrefix).draft,
            draft,
          ),
        ),
      onAreaEdited: (key, index, ring) =>
        updateDraft((draft) => layout.setBlockArea(draft, key, index, ring)),
      // The map's remove tool, like production's: no confirm.
      onAreaRemoved: (key, index) =>
        updateDraft((draft) => {
          const location = layout.findBlockLocation(draft, key);
          const next = layout.removeBlockArea(draft, key, index);
          if (location && !layout.findBlockLocation(next, key)) {
            showToast(
              t('new_season_block_deleted', { name: location.block.name }),
            );
          }
          return next;
        }),
      onAddField: () =>
        updateDraft((draft) => layout.addEmptyField(draft, fieldPrefix)),
      onGroup: () => {
        updateDraft((draft) =>
          layout.groupBlocks(draft, selection.selectedKeys, fieldPrefix),
        );
        selection.clear();
      },
      onUngroup: () => {
        updateDraft((draft) =>
          layout.ungroupField(draft, selection.selectedFieldKey),
        );
        selection.clear();
      },
    }),
    [
      updateDraft,
      selection,
      setDeleteRequest,
      showToast,
      t,
      blockPrefix,
      fieldPrefix,
    ],
  );
};

// PUT the layout. Resolves once saved and the tree re-read; a save whose
// re-read failed reloads the tree and rejects with the reload message.
const useSaveLayout = ({
  distributionId,
  farmingUnitId,
  draft,
  reloadTree,
  t,
}) => {
  const dispatch = useDispatch();
  return () => {
    if (layout.findInvalidName(draft)) {
      return Promise.reject(new Error(t('new_season_invalid_names')));
    }
    return Promise.resolve(
      dispatch(
        save_new_season_layout(
          distributionId,
          farmingUnitId,
          layout.buildLayoutBody(draft),
        ),
      ),
    ).catch((error) => {
      if (error?.saved) {
        reloadTree();
        const reloading = new Error(
          t('new_season_saved_reload_failed', { message: error.message }),
        );
        reloading.isReloading = true;
        throw reloading;
      }
      throw error;
    });
  };
};

// Production's farming unit modal "Update": update_farming_unit with a
// whole-unit body, and the "Farming Unit Edited" event. The V1 thunk reports
// only through `farming_unit_actions`, so the promise settles on it.
const useSaveFarmingUnit = ({ distributionId, original }) => {
  const dispatch = useDispatch();
  const writeStatus = useV1FarmingUnitWriteStatus();
  const pendingRef = useRef(null);
  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || writeStatus?.type !== 'UPDATE') {
      return;
    }
    if (writeStatus.status === 'success' || writeStatus.status === 'fail') {
      pendingRef.current = null;
      dispatch(distributionActions.clear_farming_unit_action());
      if (writeStatus.status === 'success') {
        pending.resolve();
      } else {
        pending.reject(new Error(writeStatus.error || ''));
      }
    }
  }, [writeStatus, dispatch]);
  return (body) =>
    new Promise((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      dispatch(distributionActions.update_farming_unit(distributionId, body));
      const changes = unitRules.farmingUnitChanges(body, original);
      if (Object.keys(changes).length > 0) {
        trackMixpanel('Farming Unit Edited', { Changes: changes });
      }
    });
};

// The body the open tab's save sends (Meta data / Sensors), or null (Fields).
const unitSaveBodyFor = (tab, unitDraft) => {
  if (!unitDraft.unit) {
    return null;
  }
  if (tab === TABS.META) {
    return unitRules.metaSaveBody(unitDraft.unit, unitDraft.original);
  }
  if (tab === TABS.SENSORS) {
    return unitRules.sensorsSaveBody(unitDraft.unit, unitDraft.original);
  }
  return null;
};

// "Save changes" saves the open tab only. A farming unit save re-reads the
// tree (and v2/distributions, for the names); the layout save already does.
const useSave = ({
  tab,
  parts,
  unitDraft,
  saveUnit,
  saveLayout,
  reloadTree,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const save = async () => {
    const part = parts[tab];
    const body = unitSaveBodyFor(tab, unitDraft);
    part.savingRef.current = true;
    setIsSaving(true);
    try {
      if (body) {
        await saveUnit(body);
        dispatch(load_new_season_distributions());
        await reloadTree();
      } else {
        await saveLayout();
      }
      showToast(t('new_season_changes_saved'));
    } catch (error) {
      if (!error?.isReloading) {
        part.savingRef.current = false;
      }
      showToast(error?.message || String(error), 'error');
    } finally {
      setIsSaving(false);
    }
  };
  return { isSaving, save };
};

// Why "Save changes" is off, if it is: the tab's own reason first.
const saveNoteFor = ({
  tab,
  tree,
  canWriteRole,
  canEditUnit,
  unitBody,
  blockingReason,
  t,
}) => {
  if (tab === TABS.FIELDS) {
    if (!tree.hierarchy_writes_enabled) {
      return t('new_season_writes_disabled');
    }
    return canWriteRole ? null : t('new_season_read_only');
  }
  if (!canEditUnit) {
    return t('new_season_read_only');
  }
  if (tab === TABS.SENSORS && blockingReason) {
    return blockingReason;
  }
  return unitBody && unitRules.isFarmingUnitIncomplete(unitBody)
    ? t('new_season_unit_incomplete')
    : null;
};

// The list's bottom (Figma "Group / Ungroup"): with blocks selected,
// Delete + Group; with a field selected, Delete + Ungroup; else Add field.
const FieldsFooter = ({ canEdit, selection, selectedField, handlers }) => {
  const { t } = useTranslation();
  const blockCount = selection.selectedKeys.length;
  if (!canEdit || (blockCount === 0 && !selectedField)) {
    return (
      <button
        type='button'
        className='ns-btn ns-btn-outline ns-btn-block'
        disabled={!canEdit}
        onClick={handlers.onAddField}
      >
        {t('new_season_add_field')}
      </button>
    );
  }
  return (
    <div className='ns-list-footer'>
      <button
        type='button'
        className='ns-btn ns-btn-danger'
        onClick={handlers.onRequestDelete}
      >
        {t('delete')}
      </button>
      {selectedField ? (
        <button
          type='button'
          className='ns-btn ns-btn-outline'
          disabled={selectedField.blocks.length === 0}
          onClick={handlers.onUngroup}
        >
          {t('new_season_ungroup')}
        </button>
      ) : (
        <button
          type='button'
          className='ns-btn ns-btn-outline'
          disabled={blockCount < 2}
          onClick={handlers.onGroup}
        >
          {t('new_season_group')}
        </button>
      )}
    </div>
  );
};

const FieldsTab = ({ draft, canEdit, selection, selectedField, handlers }) => (
  <>
    <KmzDropZone disabled={!canEdit} onAreas={handlers.onAreasDrawn} />
    <div className='ns-list-scroll'>
      <BlockList
        draft={draft}
        selectedKeys={selection.selectedKeys}
        selectedFieldKey={selection.selectedFieldKey}
        readOnly={!canEdit}
        handlers={handlers}
      />
    </div>
    <FieldsFooter
      canEdit={canEdit}
      selection={selection}
      selectedField={selectedField}
      handlers={handlers}
    />
  </>
);

// The confirm modal's title and text for a delete request.
const deleteModalTextFor = (request, t) => {
  if (request?.fieldKey) {
    return {
      title: t('new_season_delete_field_title'),
      text: t('new_season_delete_field_text'),
    };
  }
  if (request?.blockKeys?.length > 1) {
    return {
      title: t('new_season_delete_blocks_title', {
        count: request.blockKeys.length,
      }),
      text: t('new_season_delete_blocks_text'),
    };
  }
  return {
    title: t('new_season_delete_block_title'),
    text: t('new_season_delete_block_text'),
  };
};

// Applies a confirmed delete request to the draft; returns the toast text.
const applyDeleteRequest = (request, draft, updateDraft, t) => {
  if (request.fieldKey) {
    const field = draft.fields.find(
      (current) => current.key === request.fieldKey,
    );
    updateDraft((current) => layout.deleteField(current, request.fieldKey));
    return field ? t('new_season_block_deleted', { name: field.name }) : null;
  }
  const names = request.blockKeys
    .map((key) => layout.findBlockLocation(draft, key)?.block.name)
    .filter(Boolean);
  updateDraft((current) => layout.deleteBlocks(current, request.blockKeys));
  return names.length
    ? t('new_season_block_deleted', { name: names.join(', ') })
    : null;
};

const FarmingUnitEditor = ({
  distributionId,
  farmingUnitId,
  tree,
  reloadTree,
  setDirty,
}) => {
  const { t } = useTranslation();
  const distributionName = useDistributionName(distributionId);
  const [tab, setTab] = useState(TABS.FIELDS);
  const metaPart = useEditPart();
  const fieldsPart = useEditPart();
  const sensorsPart = useEditPart();
  const parts = useMemo(
    () => ({
      [TABS.META]: metaPart,
      [TABS.FIELDS]: fieldsPart,
      [TABS.SENSORS]: sensorsPart,
    }),
    [metaPart, fieldsPart, sensorsPart],
  );
  const [draft, updateDraft] = useDraft(tree, fieldsPart);
  const unitDraft = useUnitDraft(tree, metaPart, sensorsPart);
  const selection = useSelection();
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [isSensorDetailsOpen, setIsSensorDetailsOpen] = useState(false);
  const [blockingReason, setBlockingReason] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const { toast, showToast } = useToast();
  const canWriteRole = WRITE_ROLES.includes(tree.role);
  const canEdit = Boolean(tree.hierarchy_writes_enabled) && canWriteRole;
  const canEditUnit = Boolean(tree.can_edit_farming_unit && unitDraft.unit);
  const handlers = useLayoutHandlers({
    updateDraft,
    selection,
    setDeleteRequest,
    showToast,
    t,
  });
  const saveLayout = useSaveLayout({
    distributionId,
    farmingUnitId,
    draft,
    reloadTree,
    t,
  });
  const saveUnit = useSaveFarmingUnit({
    distributionId,
    original: unitDraft.original,
  });
  const { isSaving, save } = useSave({
    tab,
    parts,
    unitDraft,
    saveUnit,
    saveLayout,
    reloadTree,
    showToast,
    t,
  });
  useDeleteKey(
    tab === TABS.FIELDS ? selection.selectedKeys : [],
    canEdit,
    handlers.onRequestDelete,
  );
  const isAnyDirty =
    metaPart.isDirty || fieldsPart.isDirty || sensorsPart.isDirty;
  useEffect(() => {
    setDirty(isAnyDirty);
  }, [isAnyDirty, setDirty]);
  // A re-read can drop the selected sensor (deleted and saved).
  const unitSensors = unitDraft.unit?.sensors;
  useEffect(() => {
    setSelectedSensor((serial) =>
      serial && unitSensors?.[serial] ? serial : null,
    );
  }, [unitSensors]);

  const mapBlocks = useMemo(() => layout.allBlocks(draft), [draft]);
  const toggleSensor = useCallback(
    (serial) =>
      setSelectedSensor((current) => (current === serial ? null : serial)),
    [],
  );
  const sensorMarkers = useMemo(
    () =>
      tab === TABS.SENSORS
        ? farmingUnitSensorMarkers(
            unitDraft.unit?.sensors,
            selectedSensor,
            toggleSensor,
          )
        : [],
    [tab, unitDraft.unit, selectedSensor, toggleSensor],
  );
  const selectedField = draft.fields.find(
    (field) => field.key === selection.selectedFieldKey,
  );

  const confirmDelete = () => {
    const message = applyDeleteRequest(deleteRequest, draft, updateDraft, t);
    selection.clear();
    setDeleteRequest(null);
    if (message) {
      showToast(message);
    }
  };

  // The open tab's save: a farming unit body must be savable as production's
  // modal requires (complete, permitted; Sensors: no open sensor edit).
  const unitBody = unitSaveBodyFor(tab, unitDraft);
  const isTabSavable = unitBody
    ? canEditUnit &&
      !unitRules.isFarmingUnitIncomplete(unitBody) &&
      !(tab === TABS.SENSORS && blockingReason)
    : canEdit;
  const canSave = !isSaving && parts[tab].isDirty && isTabSavable;
  const saveNote = saveNoteFor({
    tab,
    tree,
    canWriteRole,
    canEditUnit,
    unitBody,
    blockingReason,
    t,
  });

  const tabs = [
    {
      value: TABS.META,
      label: t('new_season_tab_meta_data'),
      disabled: !unitDraft.unit,
      hasUnsavedChanges: metaPart.isDirty,
    },
    {
      value: TABS.FIELDS,
      label: t('new_season_tab_fields'),
      hasUnsavedChanges: fieldsPart.isDirty,
    },
    {
      value: TABS.SENSORS,
      label: t('new_season_tab_sensors'),
      disabled: !unitDraft.unit,
      hasUnsavedChanges: sensorsPart.isDirty,
    },
  ];

  return (
    <div className='ns-editor'>
      <div
        className={
          'ns-left-card' +
          (tab === TABS.SENSORS && isSensorDetailsOpen
            ? ' ns-left-card-wide'
            : '')
        }
      >
        <div className='ns-card-title'>
          {t('new_season_manage_farming_unit')}
        </div>
        <SegmentedTabs options={tabs} value={tab} onChange={setTab} />
        {tab === TABS.META && (
          <FarmingUnitMetaTab
            farmingUnit={unitDraft.unit}
            readOnly={!canEditUnit}
            onChange={unitDraft.onChange}
            distributionName={distributionName}
          />
        )}
        {tab === TABS.FIELDS && (
          <FieldsTab
            draft={draft}
            canEdit={canEdit}
            selection={selection}
            selectedField={selectedField}
            handlers={handlers}
          />
        )}
        {tab === TABS.SENSORS && (
          <FarmingUnitSensorsTab
            sensors={unitDraft.unit.sensors}
            selectedSerial={selectedSensor}
            onSelect={toggleSensor}
            onChangeSensors={unitDraft.onChangeSensors}
            onBlockingChange={setBlockingReason}
            onDetailsOpenChange={setIsSensorDetailsOpen}
            readOnly={!canEditUnit}
            farmingUnitName={unitDraft.original?.name}
            distributionName={distributionName}
          />
        )}
      </div>
      <div className='ns-right-column'>
        <div className='ns-card ns-right-card'>
          <div className='ns-title-bar ns-title-static'>
            {unitRules.farmingUnitEditTitle(unitDraft.unit)}
          </div>
          <BlocksMap
            className='ns-map-large'
            fitKey={distributionId + '/' + farmingUnitId}
            blocks={mapBlocks}
            selectedKeys={tab === TABS.FIELDS ? selection.selectedKeys : []}
            onBlockClick={
              tab === TABS.FIELDS ? selection.selectBlock : undefined
            }
            showDrawTools={tab === TABS.FIELDS && canEdit}
            onDrawn={(areas) => handlers.onAreasDrawn([areas[0]])}
            onAreaEdited={handlers.onAreaEdited}
            onAreaRemoved={handlers.onAreaRemoved}
            markers={sensorMarkers}
          />
        </div>
        <div className='ns-save-row'>
          {saveNote && <div className='ns-note'>{saveNote}</div>}
          <button
            type='button'
            className='ns-btn ns-btn-primary'
            disabled={!canSave}
            onClick={save}
          >
            {t('new_season_save_changes')}
            {isSaving && (
              <span
                className='spinner-border spinner-border-sm ml-2'
                role='status'
              />
            )}
          </button>
        </div>
      </div>
      <ConfirmDeleteBlockModal
        isOpen={Boolean(deleteRequest)}
        {...deleteModalTextFor(deleteRequest, t)}
        onCancel={() => setDeleteRequest(null)}
        onConfirm={confirmDelete}
      />
      <Toast toast={toast} />
    </div>
  );
};

module.exports = FarmingUnitEditor;
